/* Savage Gear — comportamiento del header: mega-menús y cajón móvil.
   Sin dependencias. Toda la animación vive en CSS para que corra fuera del
   hilo principal; aquí solo se conmutan atributos. */

(() => {
  'use strict';

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ---------------------------------------------------------------------- */
  /* Mega-menús                                                             */
  /* ---------------------------------------------------------------------- */

  class SgNav {
    constructor(root) {
      this.root = root;
      this.items = [...root.querySelectorAll('.sg-nav__item')].filter((i) =>
        i.querySelector('.sg-mega')
      );
      this.openItem = null;
      this.closeTimer = null;
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.items.forEach((item) => {
        const trigger = item.querySelector('.sg-nav__link');
        if (!trigger) return;

        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle(item);
        });

        // El hover solo aplica con puntero fino. En táctil dispara al tocar y
        // abriría el menú justo cuando el usuario quiso navegar.
        item.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse' || !this.finePointer.matches) return;
          clearTimeout(this.closeTimer);
          this.open(item);
        });

        item.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse' || !this.finePointer.matches) return;
          // Pequeña gracia para cruzar el hueco entre el ítem y el panel sin
          // que se cierre a mitad de camino.
          this.closeTimer = setTimeout(() => this.close(item), 120);
        });

        // Si el foco sale del ítem por teclado, se cierra
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) this.close(item);
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !this.openItem) return;
        const trigger = this.openItem.querySelector('.sg-nav__link');
        this.close(this.openItem);
        trigger && trigger.focus();
      });

      document.addEventListener('pointerdown', (e) => {
        if (this.openItem && !this.openItem.contains(e.target)) this.close(this.openItem);
      });

      /* Se coloca también al cargar y al redimensionar, no solo al abrir.
         Un panel cerrado sigue ocupando su posición: `visibility: hidden` no
         lo saca del cálculo del ancho de la página, así que el de Categorías
         desbordaba 38px y aparecía barra de scroll horizontal sin que nada
         se viera fuera de sitio. */
      this.recolocarTodos();
      let t;
      window.addEventListener('resize', () => {
        clearTimeout(t);
        t = setTimeout(() => this.recolocarTodos(), 150);
      });
    }

    recolocarTodos() {
      this.items.forEach((item) => this.keepInViewport(item));
    }

    open(item) {
      if (this.openItem && this.openItem !== item) this.close(this.openItem);
      item.dataset.open = 'true';
      const t = item.querySelector('.sg-nav__link');
      t && t.setAttribute('aria-expanded', 'true');
      this.openItem = item;
      this.keepInViewport(item);
    }

    close(item) {
      item.dataset.open = 'false';
      const t = item.querySelector('.sg-nav__link');
      t && t.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) this.openItem = null;
    }

    toggle(item) {
      item.dataset.open === 'true' ? this.close(item) : this.open(item);
    }

    /* Mantiene el panel dentro del viewport.
       Antes esto volteaba el anclaje al borde derecho del disparador cuando
       se salía por la derecha, pero con un panel de 920px eso lo sacaba
       400px por la izquierda. Ahora se calcula el desplazamiento exacto y se
       corrige solo lo necesario, comprobando los dos bordes. */
    keepInViewport(item) {
      const panel = item.querySelector('.sg-mega');
      if (!panel) return;

      const MARGEN = 16;
      panel.style.left = '0px';

      /* Se mide por layout (offsetWidth/offsetLeft) y no con
         getBoundingClientRect: en el instante en que se llama, el panel
         todavía tiene el scale(0.985) del estado cerrado, así que su caja
         medida sale más chica que la real y el desplazamiento queda corto. */
      const t = item.getBoundingClientRect();
      const anchoReal = panel.offsetWidth;
      const izqReal = t.left + panel.offsetLeft;
      const derReal = izqReal + anchoReal;
      let desplazar = 0;

      if (derReal > window.innerWidth - MARGEN) {
        desplazar = window.innerWidth - MARGEN - derReal;
      }
      // Se comprueba después de corregir la derecha: si el panel es más ancho
      // que el hueco disponible, manda el borde izquierdo.
      if (izqReal + desplazar < MARGEN) {
        desplazar = MARGEN - izqReal;
      }

      panel.style.left = Math.round(desplazar) + 'px';

      /* El origen sigue apuntando al disparador aunque el panel se haya
         corrido: si no, un panel desplazado escalaría desde una esquina que
         no tiene nada que ver con el ítem que lo abrió. */
      const origenX = Math.round(t.left - (izqReal + desplazar) + t.width / 2);
      panel.style.transformOrigin = origenX + 'px top';
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Cajón móvil                                                            */
  /* ---------------------------------------------------------------------- */

  class SgDrawer {
    constructor(drawer) {
      this.drawer = drawer;
      this.overlay = document.querySelector('.sg-drawer__overlay');
      this.lastFocus = null;

      document.querySelectorAll('[data-sg-drawer-open]').forEach((b) =>
        b.addEventListener('click', () => this.open())
      );
      document.querySelectorAll('[data-sg-drawer-close]').forEach((b) =>
        b.addEventListener('click', () => this.close())
      );
      this.overlay && this.overlay.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (this.drawer.dataset.open !== 'true') return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trapFocus(e);
      });

      // Acordeones internos
      drawer.querySelectorAll('.sg-acc__trigger').forEach((t) =>
        t.addEventListener('click', () => {
          const open = t.getAttribute('aria-expanded') === 'true';
          t.setAttribute('aria-expanded', String(!open));
        })
      );
    }

    open() {
      this.lastFocus = document.activeElement;
      this.drawer.dataset.open = 'true';
      this.overlay && (this.overlay.dataset.open = 'true');
      this.drawer.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden';
      const first = this.drawer.querySelector(FOCUSABLE);
      first && first.focus();
    }

    close() {
      this.drawer.dataset.open = 'false';
      this.overlay && (this.overlay.dataset.open = 'false');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.lastFocus && this.lastFocus.focus();
    }

    trapFocus(e) {
      const f = [...this.drawer.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null
      );
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ---------------------------------------------------------------------- */

  const init = () => {
    const nav = document.querySelector('.sg-nav');
    if (nav) new SgNav(nav);
    const drawer = document.querySelector('.sg-drawer');
    if (drawer) new SgDrawer(drawer);
  };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

  // El editor de temas recarga secciones sin recargar la página
  document.addEventListener('shopify:section:load', init);
})();
